import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Save, Loader2, ShieldCheck, ShieldAlert, ExternalLink, AlertTriangle,
  CheckCircle2, XCircle, Copy, FlaskConical, Zap,
} from "lucide-react";

/**
 * Admin → Billing → Stripe sub-tab.
 *
 * Stores publishable + secret + webhook signing secrets for BOTH test and
 * live modes. Active mode is selectable via radios. Secrets are write-only
 * (we never echo them back); empty input = leave existing value untouched.
 */
export default function StripeConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/billing/stripe-config");
      setCfg(data);
      setDraft({});
    } catch {
      toast.error("Failed to load Stripe config");
    }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const v = (k) => (draft[k] !== undefined ? draft[k] : cfg[k]);
  const set = (k, val) => setDraft((d) => ({ ...d, [k]: val }));
  const dirty = Object.keys(draft).length > 0;
  const mode = v("mode") || "test";
  const isLive = mode === "live";

  const buildPayload = () => {
    const p = {};
    if (draft.mode) p.stripe_mode = draft.mode;
    if (draft.pub_key_test !== undefined) p.stripe_pub_key_test = draft.pub_key_test;
    if (draft.pub_key_live !== undefined) p.stripe_pub_key_live = draft.pub_key_live;
    // Secrets only sent if non-empty
    if (draft.stripe_secret_test) p.stripe_secret_test = draft.stripe_secret_test;
    if (draft.stripe_secret_live) p.stripe_secret_live = draft.stripe_secret_live;
    if (draft.stripe_whsec_test) p.stripe_whsec_test = draft.stripe_whsec_test;
    if (draft.stripe_whsec_live) p.stripe_whsec_live = draft.stripe_whsec_live;
    return p;
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/billing/stripe-config", buildPayload());
      setCfg(data);
      setDraft({});
      toast.success("Stripe configuration saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); setConfirmLive(false); }
  };

  const save = async () => {
    if ((draft.mode || cfg.mode) === "live" && draft.mode === "live" && !confirmLive) {
      setConfirmLive(true);
      return;
    }
    await doSave();
  };

  const test = async () => {
    if (dirty) {
      toast.info("Save your changes before testing the connection.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/billing/stripe-config/test");
      toast.success(`Connection OK · ${data.mode} mode`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test failed");
      await load();
    } finally { setTesting(false); }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(cfg.webhook_url);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="stripe-config-panel">
      {/* Header */}
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">Stripe configuration</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Paste your own Stripe keys. We never use a platform-managed key — all checkouts and webhooks run through the keys you enter here. Secrets are encrypted at rest (Fernet) and never returned to the browser.
        </p>
        <a
          href="https://dashboard.stripe.com/test/apikeys"
          target="_blank" rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline"
          data-testid="stripe-dash-link"
        >
          Open Stripe Dashboard → API keys <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Mode selector + status */}
      <div className="border border-border bg-card rounded-sm p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Active mode</div>
            <div className="mt-2 flex items-center gap-2">
              <ModeRadio active={mode === "test"} label="Test" onClick={() => set("mode", "test")} testId="stripe-mode-test" />
              <ModeRadio active={mode === "live"} label="Live" onClick={() => set("mode", "live")} variant="live" testId="stripe-mode-live" />
            </div>
          </div>
          <StatusBadge mode={mode} ok={cfg.last_test_ok} at={cfg.last_test_at} />
        </div>

        {isLive && (
          <div className="border border-rose-500/40 bg-rose-500/10 text-rose-800 dark:text-rose-200 rounded-sm p-3 text-xs flex gap-2" data-testid="stripe-live-warning">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <b>Live mode</b> — Stripe will make <b>real charges</b> against real customer cards. Make sure your live keys are correct and your webhook endpoint is verified before announcing this to users.
            </div>
          </div>
        )}

        {cfg.last_test_at && (
          <div className="text-xs font-mono flex items-center gap-2 flex-wrap" data-testid="stripe-last-test">
            {cfg.last_test_ok
              ? <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />ok</span>
              : <span className="text-destructive flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />failed</span>
            }
            <span className="text-muted-foreground">{cfg.last_test_mode} mode · {new Date(cfg.last_test_at).toLocaleString()}</span>
            {cfg.last_test_error && <span className="text-destructive ml-1 truncate" title={cfg.last_test_error}>· {cfg.last_test_error}</span>}
          </div>
        )}
      </div>

      {/* Test keys */}
      <KeySet
        title="Test keys"
        prefix="test"
        v={v} set={set} cfg={cfg}
        accent="emerald"
        hint="From Stripe Dashboard while in Test Mode. Used when active mode = Test."
      />

      {/* Live keys */}
      <KeySet
        title="Live keys"
        prefix="live"
        v={v} set={set} cfg={cfg}
        accent="rose"
        hint="From Stripe Dashboard with the Test-Mode toggle OFF. Real money — handle with care."
      />

      {/* Webhook URL */}
      <div className="border border-border bg-card rounded-sm p-5" data-testid="stripe-webhook-info">
        <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Webhook endpoint URL</div>
        <p className="text-xs text-muted-foreground mt-1">
          In Stripe Dashboard → Developers → Webhooks → <b>Add endpoint</b>, paste this URL and subscribe to the events <span className="font-mono">checkout.session.completed</span>, <span className="font-mono">charge.refunded</span>, <span className="font-mono">payment_intent.payment_failed</span>. After creating it, copy the signing secret (<span className="font-mono">whsec_…</span>) into the matching field above.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded-sm border border-border bg-muted/30 text-xs font-mono truncate" data-testid="webhook-url-display">{cfg.webhook_url}</code>
          <Button size="sm" variant="outline" onClick={copyWebhook} className="rounded-sm" data-testid="copy-webhook-url">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
          </Button>
        </div>
      </div>

      {/* Action bar */}
      <div className="sticky bottom-0 -mx-1 px-1 py-3 bg-background border-t border-border flex items-center gap-2 flex-wrap">
        <Button onClick={save} disabled={!dirty || saving} className="rounded-sm" data-testid="stripe-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {saving ? "Saving…" : "Save configuration"}
        </Button>
        <Button onClick={test} disabled={testing || dirty} variant="outline" className="rounded-sm" data-testid="stripe-test"
          title={dirty ? "Save before testing" : `Validate the ${mode}-mode key with Stripe`}>
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FlaskConical className="h-3.5 w-3.5 mr-1.5" />}
          Test {mode} connection
        </Button>
        {dirty && <span className="text-xs font-mono text-amber-600 dark:text-amber-400">unsaved changes</span>}
      </div>

      {/* Confirm-live modal */}
      {confirmLive && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm grid place-items-center p-4" data-testid="confirm-live-modal">
          <div className="w-full max-w-md bg-card border-2 border-rose-500/40 rounded-sm p-6">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              <h3 className="font-display font-bold tracking-tight text-lg">Switch to LIVE mode?</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              From this moment, every Buy-with-Stripe click will produce a <b>real charge</b> on a customer card. Make sure:
            </p>
            <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
              <li>Live secret key is saved</li>
              <li>Live webhook endpoint exists in Stripe Dashboard</li>
              <li>Live webhook signing secret is saved</li>
              <li>You've tested at least one live charge with a real card or refunded it</li>
            </ul>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmLive(false)} className="rounded-sm" data-testid="confirm-live-cancel">Cancel</Button>
              <Button onClick={doSave} disabled={saving} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-live-yes">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Yes, go live
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ModeRadio({ active, label, onClick, variant = "test", testId }) {
  const liveStyle = variant === "live";
  const cls = active
    ? (liveStyle ? "border-rose-500 bg-rose-500/10 text-rose-700 dark:text-rose-300" : "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300")
    : "border-border bg-card text-muted-foreground hover:text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-sm border text-xs font-mono uppercase tracking-wider font-semibold transition ${cls}`}
      data-testid={testId}
    >
      {active && (liveStyle ? <ShieldAlert className="h-3 w-3 inline mr-1" /> : <ShieldCheck className="h-3 w-3 inline mr-1" />)}
      {label}
    </button>
  );
}

function StatusBadge({ mode, ok, at }) {
  if (!at) return <span className="text-[11px] font-mono text-muted-foreground">never tested</span>;
  if (ok) return (
    <span className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
      <CheckCircle2 className="h-3 w-3" /> {mode} mode · verified
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border border-destructive/40 bg-destructive/10 text-destructive">
      <XCircle className="h-3 w-3" /> {mode} mode · failed
    </span>
  );
}

function KeySet({ title, prefix, v, set, cfg, accent, hint }) {
  const isLive = prefix === "live";
  const pubKey = `pub_key_${prefix}`;
  const secField = `stripe_secret_${prefix}`;
  const whsField = `stripe_whsec_${prefix}`;
  const secConfigured = !!cfg[`secret_${prefix}_configured`];
  const whsConfigured = !!cfg[`whsec_${prefix}_configured`];

  const borderCls = accent === "rose"
    ? "border-rose-500/30 bg-rose-500/[0.03]"
    : "border-emerald-500/30 bg-emerald-500/[0.03]";

  return (
    <div className={`border rounded-sm p-5 space-y-4 ${borderCls}`} data-testid={`keyset-${prefix}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold tracking-tight">{title}</div>
          <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-mono">
          <Indicator ok={!!v(pubKey)} label="pub" />
          <Indicator ok={secConfigured} label="sk" />
          <Indicator ok={whsConfigured} label="whsec" />
        </div>
      </div>

      <Field label="Publishable key" hint={isLive ? "pk_live_…" : "pk_test_…"}>
        <Input
          value={v(pubKey) || ""}
          onChange={(e) => set(pubKey, e.target.value)}
          placeholder={isLive ? "pk_live_..." : "pk_test_..."}
          className="rounded-sm font-mono"
          data-testid={`pub-${prefix}`}
        />
      </Field>

      <Field
        label="Secret key"
        hint={secConfigured ? "Configured · leave blank to keep existing" : (isLive ? "sk_live_…" : "sk_test_…")}
      >
        <Input
          type="password"
          autoComplete="new-password"
          value={v(secField) || ""}
          onChange={(e) => set(secField, e.target.value)}
          placeholder={secConfigured ? "••••••••" : (isLive ? "sk_live_..." : "sk_test_...")}
          className="rounded-sm font-mono"
          data-testid={`sec-${prefix}`}
        />
      </Field>

      <Field
        label="Webhook signing secret"
        hint={whsConfigured ? "Configured · leave blank to keep existing" : "From Stripe Dashboard → Developers → Webhooks → your endpoint"}
      >
        <Input
          type="password"
          autoComplete="new-password"
          value={v(whsField) || ""}
          onChange={(e) => set(whsField, e.target.value)}
          placeholder={whsConfigured ? "••••••••" : "whsec_..."}
          className="rounded-sm font-mono"
          data-testid={`whs-${prefix}`}
        />
      </Field>
    </div>
  );
}

function Indicator({ ok, label }) {
  return (
    <span className={`px-1.5 py-0.5 rounded-sm border ${ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-border text-muted-foreground"}`}>
      {label}
    </span>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-mono">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1 font-mono">{hint}</p>}
    </div>
  );
}
